import {
  applyBlock,
  cloneBlock,
  removeBlock,
  updateBlock,
  type Block,
  type BlockMember,
  type BlockSetRow,
  type ComposedBlock,
  type Draft,
} from "@jackedlogs/shared";

// Pure form → blocks conversion for the composer's ＋ AGGIUNGI / Aggiorna.
//
// The form holds N members + the D/W chip selections. Grouped (the picker's
// Superset tab) → ONE multi-member block (applyBlock asserts the grouping);
// ungrouped multi-select → one single-member block PER exercise, sharing the
// same days/weeks — the picker's Esercizi tab adds standalone slots, exactly
// like it does in the logger. Slot ids/positions stay empty: applyBlock
// appends fresh slots, updateBlock reconciles ids from the old block.

export const blankBlockRow = (): BlockSetRow => ({
  reps: "5",
  intensityKind: "percent-of-max",
  intensityValue: "70",
  note: "",
});

export const blankBlockMember = (
  exerciseId: string,
  exerciseName: string | undefined,
): BlockMember => ({
  exerciseId,
  exerciseName,
  setRows: [blankBlockRow()],
  instructions: "",
  amrapLastSet: false,
  maxAttempt: false,
  rpeEnabled: false,
  rirEnabled: false,
});

const cloneMember = (m: BlockMember): BlockMember => ({
  ...m,
  setRows: m.setRows.map((r) => ({ ...r })),
});

export function composeBlocks(
  members: ReadonlyArray<BlockMember>,
  grouped: boolean,
  days: ReadonlyArray<number>,
  weeks: ReadonlyArray<number>,
): ComposedBlock[] {
  const dayList = [...new Set(days)].sort((a, b) => a - b);
  const weekList = [...new Set(weeks)].sort((a, b) => a - b);
  if (members.length === 0 || dayList.length === 0 || weekList.length === 0)
    return [];
  const placementsFor = (n: number) =>
    dayList.map((dayIdx) => ({
      dayIdx,
      slotIds: Array.from({ length: n }, () => ""),
      positions: Array.from({ length: n }, () => -1),
      group: null,
    }));
  if (grouped) {
    return [
      {
        kind: "composed",
        members: members.map(cloneMember),
        weeks: weekList,
        placements: placementsFor(members.length),
      },
    ];
  }
  return members.map((m) => ({
    kind: "composed",
    members: [cloneMember(m)],
    weeks: [...weekList],
    placements: placementsFor(1),
  }));
}

// Compact W/D chip-set label: consecutive runs compress to ranges
// ("W1-W3·W5"). Used by the block list rows.
export function rangeLabel(prefix: string, sorted: ReadonlyArray<number>): string {
  if (sorted.length === 0) return "";
  const parts: string[] = [];
  let start = sorted[0]!;
  let prev = sorted[0]!;
  const flush = () => {
    parts.push(
      start === prev
        ? `${prefix}${start + 1}`
        : `${prefix}${start + 1}-${prefix}${prev + 1}`,
    );
  };
  for (const w of sorted.slice(1)) {
    if (w === prev + 1) {
      prev = w;
      continue;
    }
    flush();
    start = w;
    prev = w;
  }
  flush();
  return parts.join("·");
}

const verbatimActiveWeeks = (b: Extract<Block, { kind: "verbatim" }>): number[] =>
  b.slot.inactiveWeeks.map((off, w) => (off ? -1 : w)).filter((w) => w >= 0);

// Duplicate guard (Antenore 2026-06-11: "vietare due esercizi uguali
// nello stesso giorno e settimana"): returns the first (slug, day) that
// would collide if `slugs` were placed on `days` × `weeks`. `editing`
// skips the block being updated AND its ramp siblings (segments of the
// same slot share slot ids — extending a segment into a sibling's weeks
// is an edit of the same slot, not a duplicate).
export function findSlugConflict(
  blocks: ReadonlyArray<Block>,
  editing: ComposedBlock | null,
  slugs: ReadonlyArray<string>,
  days: ReadonlyArray<number>,
  weeks: ReadonlyArray<number>,
): { slug: string; dayIdx: number } | null {
  const daySet = new Set(days);
  const weekSet = new Set(weeks);
  const slugSet = new Set(slugs);
  const editingSlotIds = new Set(
    editing?.placements.flatMap((p) => p.slotIds.filter((id) => id !== "")) ?? [],
  );
  for (const b of blocks) {
    if (b === editing) continue;
    if (b.kind === "verbatim") {
      if (!slugSet.has(b.slot.exerciseId) || !daySet.has(b.dayIdx)) continue;
      if (editing && b.slot.id && editingSlotIds.has(b.slot.id)) continue;
      if (verbatimActiveWeeks(b).some((w) => weekSet.has(w)))
        return { slug: b.slot.exerciseId, dayIdx: b.dayIdx };
      continue;
    }
    const sharesSlot =
      editing !== null &&
      b.placements.some((p) => p.slotIds.some((id) => id !== "" && editingSlotIds.has(id)));
    if (sharesSlot) continue;
    const hitDay = b.placements.find((p) => daySet.has(p.dayIdx));
    if (!hitDay) continue;
    if (!b.weeks.some((w) => weekSet.has(w))) continue;
    const m = b.members.find((mm) => slugSet.has(mm.exerciseId));
    if (m) return { slug: m.exerciseId, dayIdx: hitDay.dayIdx };
  }
  return null;
}

export type SlugConflict = { slug: string; dayIdx: number };
export type CloneDayPlan =
  | { ok: true; applies: Block[] }
  | { ok: false; reason: "empty" }
  | { ok: false; reason: "conflict"; conflict: SlugConflict };

// ⧉ Clona giorno, the pure half: every block placing on `fromDay`
// becomes a fresh single-placement block on `toDay` (ids/positions/
// groups re-minted by applyBlock; multi-day blocks contribute only
// their content). Refuses on the duplicate guard instead of
// half-cloning.
export function planDayClone(
  blocks: ReadonlyArray<Block>,
  fromDay: number,
  toDay: number,
  // LG-41 — same flag the commit path takes, for the same reason. Without it
  // ⧉ clone-day would be the one gesture still enforcing the 2026-06-11 rule:
  // placing a second copy of an exercise on a day by hand would succeed while
  // cloning a day that contains one would be refused, which reads as a bug
  // rather than as a rule. It fails closed, so this was never corruption.
  allowDuplicateSlugs = false,
): CloneDayPlan {
  const sources = blocks.filter((b) =>
    b.kind === "verbatim"
      ? b.dayIdx === fromDay
      : b.placements.some((p) => p.dayIdx === fromDay),
  );
  if (sources.length === 0 || fromDay === toDay)
    return { ok: false, reason: "empty" };
  for (const b of sources) {
    const slugs =
      b.kind === "verbatim"
        ? [b.slot.exerciseId]
        : b.members.map((m) => m.exerciseId);
    const weeks = b.kind === "verbatim" ? verbatimActiveWeeks(b) : b.weeks;
    const conflict = allowDuplicateSlugs
      ? null
      : findSlugConflict(blocks, null, slugs, [toDay], weeks);
    if (conflict) return { ok: false, reason: "conflict", conflict };
  }
  const applies = sources.map((b): Block => {
    const cloned = cloneBlock(b);
    if (cloned.kind === "verbatim")
      return { ...cloned, dayIdx: toDay, position: -1 };
    return {
      ...cloned,
      placements: [
        {
          dayIdx: toDay,
          slotIds: cloned.members.map(() => ""),
          positions: cloned.members.map(() => -1),
          group: null,
        },
      ],
    };
  });
  return { ok: true, applies };
}

// CD-1 — day-scoped block edit. After a ⧉ clone-day the cloned exercise is
// content-identical on N days, so deriveBlocks COALESCES it into ONE multi-day
// block (programBlocks.ts §cross-day). Editing that block from a single day's
// panel must not rewrite the sibling days: we scope the CLEAR to exactly the
// days this edit owns — the days `built` targets PLUS the day it was opened
// from (`editingDay`) — leaving `owner`'s other placements untouched. Including
// editingDay is what makes "move this exercise to another day" (untick the
// opened day, tick a new one) a MOVE and not a duplicate: without it the opened
// day would never be cleared and the exercise would sit on both. The edited
// day's content then diverges, so the next deriveBlocks splits it into its own
// block — copy-on-write, no stored block identity needed (redesign §2.1).
// `editingDay` null = a block-wide (legacy) edit: clear every owner day. Pure
// so the data path is jest-locked (composeBlocks.test.ts §day-scoped split).
export function applyBlockEditScoped(
  draft: Draft,
  owner: ComposedBlock,
  built: ComposedBlock,
  editingDay: number | null,
): Draft {
  const clearDays = new Set(built.placements.map((p) => p.dayIdx));
  if (editingDay != null) clearDays.add(editingDay);
  else for (const p of owner.placements) clearDays.add(p.dayIdx);
  const oldScoped: ComposedBlock = {
    ...owner,
    placements: owner.placements.filter((p) => clearDays.has(p.dayIdx)),
  };
  // No owner day in scope → the edit only targets brand-new days: a plain
  // apply (nothing of the owner to narrow).
  return oldScoped.placements.length === 0
    ? applyBlock(draft, built)
    : updateBlock(draft, oldScoped, built);
}

// CD-1 — day-scoped per-week split. The week-edit gesture (break W3 out of a
// segment) must also stay on the day it was opened from. `single` carries the
// edited week on the scoped day(s) only; we narrow the owner's OTHER weeks on
// those same days and re-apply, exactly like the block-wide per-week split but
// confined to `single`'s days so sibling cloned days are never touched.
export function applyWeekEditScoped(
  draft: Draft,
  owner: ComposedBlock,
  single: ComposedBlock,
): Draft {
  const days = new Set(single.placements.map((p) => p.dayIdx));
  const ownerScoped: ComposedBlock = {
    ...owner,
    placements: owner.placements.filter((p) => days.has(p.dayIdx)),
  };
  const narrowed: ComposedBlock = {
    ...ownerScoped,
    weeks: owner.weeks.filter((w) => !single.weeks.includes(w)),
  };
  return narrowed.weeks.length === 0
    ? updateBlock(draft, ownerScoped, single)
    : applyBlock(updateBlock(draft, ownerScoped, narrowed), single);
}

// CD-1 — day-scoped removal. ✕ on a fused multi-day block from one day's panel
// must delete only that day's occurrence, not the sibling days (removeBlock
// clears EVERY placement). We narrow the block to the opened day's placement
// and remove just that; the sibling placements — and their physical slots on
// other days — are never touched. Single-day/verbatim blocks fall straight
// through to removeBlock.
export function removeBlockOnDay(
  draft: Draft,
  block: Block,
  dayIdx: number,
): Draft {
  if (block.kind === "verbatim" || block.placements.length <= 1)
    return removeBlock(draft, block);
  const dayBlock: ComposedBlock = {
    ...block,
    placements: block.placements.filter((p) => p.dayIdx === dayIdx),
  };
  return dayBlock.placements.length === 0
    ? draft
    : removeBlock(draft, dayBlock);
}

// True when the form state still mirrors the block it was loaded from —
// the ramp editor refuses to switch week-segment while edits are
// uncommitted (switching reloads the form, which would silently drop
// them). Pure value comparison, field by field. `baselineDays` overrides the
// day comparison for a day-scoped edit (CD-1): the form loads pinned to the
// opened day, so the clean baseline is that day, not the block's full spread.
export function formMatchesBlock(
  members: ReadonlyArray<BlockMember>,
  days: ReadonlyArray<number>,
  weeks: ReadonlyArray<number>,
  block: ComposedBlock,
  baselineDays?: ReadonlyArray<number>,
): boolean {
  const norm = (ms: ReadonlyArray<BlockMember>) =>
    JSON.stringify(
      ms.map((m) => [
        m.exerciseId,
        m.instructions,
        m.amrapLastSet,
        m.maxAttempt,
        m.rpeEnabled,
        m.rirEnabled,
        m.setRows.map((r) => [r.reps, r.intensityKind, r.intensityValue, r.note]),
      ]),
    );
  const sortedNums = (ns: ReadonlyArray<number>) => [...new Set(ns)].sort((a, b) => a - b).join(",");
  return (
    norm(members) === norm(block.members) &&
    sortedNums(weeks) === sortedNums(block.weeks) &&
    sortedNums(days) ===
      sortedNums(baselineDays ?? block.placements.map((p) => p.dayIdx))
  );
}


// Which OLD member does each NEW member continue? Returns, per new member,
// the index of the old member whose slot it inherits, or -1 for "none".
//
// LG-41 made this matter. It used to be safe to ask "is the member at index i
// still the same exercise?", because a block could not hold the same slug
// twice: `findSlugConflict` and the picker's hide-list guaranteed it. Now a
// program block legitimately can, and index-matching gets it wrong in a way
// that is easy to hit: with members [A(e1), X, B(e1)], deleting X leaves
// [A, B], and at index 1 the old member is X while the new one is B — so B
// loses its slot and the week split re-creates it, dropping the linkage.
// Matching each new member to the first UNMATCHED old member with the same
// slug gives A→A and B→B, which is what the athlete did.
//
// ⚠️ It cannot disambiguate two SURVIVING copies of one slug from each other:
// a BlockMember carries no id, so when only one of two identical members
// survives, "which one" is unanswerable from content and this returns the
// first. That is the program-side twin of the missing PlannedExerciseEntry
// id (LG-40), and the reason the guess is at least made in a stable order.
//
// ⛔ NOT the same rule as `updateBlock` in @jackedlogs/shared, and the
// difference is deliberate, not drift: there an unmatched member falls back
// to its index and keeps that slot, here it gets -1. A ⇄-swapped member on a
// per-week edit must become its OWN slot, because a slot holds one exercise.
export function reconcileMemberSlots(
  oldMembers: ReadonlyArray<{ exerciseId: string }>,
  newMembers: ReadonlyArray<{ exerciseId: string }>,
): number[] {
  const used = new Set<number>();
  return newMembers.map((m) => {
    const j = oldMembers.findIndex(
      (om, jj) => !used.has(jj) && om.exerciseId === m.exerciseId,
    );
    if (j >= 0) used.add(j);
    return j;
  });
}
